<script lang="ts">
    import axios from 'axios';
    import Shgrid from '../../components/shgrid/shgrid.svelte';
    import type {Column} from '@/components/shgrid';
    import type { Lead } from '@/types/types';
    import * as Table from '@/components/ui/table/index.js';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';

    type Row = Lead;

    type Props = {
        view : string;
        onRowClick?: (row: Row) => void;
        relatedData?: Record<string, any>;
        title?: string | null;
    };

    let {view, onRowClick, relatedData, title = null}: Props = $props();
    const getRowLink = (row: Row) => route('leads.show', {lead: row.id});
    const includeLink = view != 'related';

    let cols = $state<Column<Row>[]>([
        {
            label: 'Ref',
            key: 'reference_number',
            cellRender: {
                type: 'text',
                label: (row) => row.reference ?? '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Name',
            key: 'name',
            cellRender: {
                type: 'text',
                label: (row) => row.name,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Status',
            key: 'status',
            cellRender: {
                type: 'custom',
                snippet: statusBadgeSnippet,
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        ...(view === 'relatedList' ? [{
            label: 'Postcode',
            key: 'postcode',
            cellRender: {
                type: 'text' as const,
                label: (row: Row) => row.address?.postcode ?? '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string' as const,
            },
        }] : []),
        {
            label: 'Phone',
            key: 'phone',
            cellRender: {
                type: 'text',
                label: (row) => row.phone ?? "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Email',
            key: 'email',
            cellRender: {
                type: 'text',
                label: (row) => row.email ?? "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Organisation',
            key: 'organisation_name',
            cellRender: {
                type: 'text',
                label: (row) => row.organisation_name ?? "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Source',
            key: 'source',
            cellRender: {
                type: 'text',
                label: (row) => row.source ?? "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Estimated Value',
            key: 'estimated_value',
            cellRender: {
                type: 'text',
                label: (row) => row.estimated_value ? `£${row.estimated_value.toFixed(2)}` : "",
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'number',
            },
        },
        {
            label: 'Created At',
            key: 'created_at',
            cellRender: {
                type: 'date',
                label: (row) => row.created_at,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        },
        ...(view === 'relatedList' ? [] : [{
            label: 'Last Updated',
            key: 'updated_at',
            cellRender: {
                type: 'date' as const,
                label: (row: Row) => row.updated_at,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date' as const,
            },
        }]),
    ]);
</script>

{#snippet statusBadgeSnippet(row: Row)}
    <Table.Cell>
        {#if row.status_label}
            <PicklistBadge label={row.status_label} color={row.status_color ?? 'gray'} />
        {:else}
            <span class="text-muted-foreground">{row.status ?? 'N/A'}</span>
        {/if}
    </Table.Cell>
{/snippet}

<div>
    <Shgrid
        fetcher={async ({ limit, offset, filters, sorter }) => {
            if (view === 'relatedList' && relatedData) {
                let relatedColumn = relatedData.column ;
                let record_id = relatedData.record_id;
                filters = { ...filters, [relatedColumn] : record_id };
            }
        const res = await axios.get(route('web-api.Lead.index', { limit, offset, filters, sorter }));
        return { data: res.data, error: null };
    }}
        limit={100}
        bind:columns={cols}
        {...(view === 'related' ? { onRowClick } : {})}
        title={title}
    />
</div>
